// Nurse Dashboard shell: renders a persistent navy sidebar (MUI v5 Drawer) and
// switches between views with internal state — no per-page App.js routes (see
// Gate-1 ADR). Ported from the Caregiver Hub NursingLayout + router.
//
// The shell is mounted twice, once per section: /nurse_dashboard (section="nurse")
// and /nursing_manager (section="manager"). Each section owns its own nav items
// and default view; the two view sets never cross-navigate.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { usePermissions } from "../login/PermissionContext";
import { getEffectiveNurseRole } from "./nurseRoleHelper";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import HotelIcon from "@mui/icons-material/Hotel";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { nurseTheme, NURSE_COLORS, initials } from "./theme";
import { NurseDataContext, normalizeAdmission, nurseAdmissionFilters } from "./NurseDataContext";
import { getAdmissions, getTheatreCalls, listOf, OT_NOT_SET_UP } from "./api/nurseApi";
import DateRangeFilter from "./components/DateRangeFilter";

import DashboardHome from "./views/DashboardHome";
import MyPatients from "./views/MyPatients";
import PatientDetail from "./views/PatientDetail";
import VitalsOverview from "./views/VitalsOverview";
import EmarOverview from "./views/EmarOverview";
import PendingConfirmations from "./views/PendingConfirmations";
import MyTasks from "./views/MyTasks";
import BedDashboard from "./views/BedDashboard";
import StaffRoster from "./views/StaffRoster";
import TransferHistory from "./views/TransferHistory";
import ShiftHandover from "./views/ShiftHandover";
import MySchedule from "./views/MySchedule";
import TheatreCalls from "./views/TheatreCalls";

const SIDEBAR_WIDTH = 248;
// Collapsed: icons only, wide enough to keep the 40px hit target centred.
const SIDEBAR_COLLAPSED_WIDTH = 72;
// Remembered across visits — a nav that silently re-expands every load is worse
// than one that never collapsed. Shared by both sections deliberately: it is a
// preference about the chrome, not about the ward.
const COLLAPSE_KEY = "nurseSidebarCollapsed";
// Set once the server says this hospital has no OT; cleared with the session.
const OT_NOT_SET_UP_KEY = "nurseOtNotSetUp";

const SECTIONS = {
  nurse: {
    title: "IPD Nursing",
    subtitle: "Caregiver Hub",
    heading: "Nurse",
    defaultView: "home",
    items: [
      { key: "home", label: "Dashboard", icon: <DashboardIcon fontSize="small" /> },
      { key: "patients", label: "My Patients", icon: <PeopleAltIcon fontSize="small" />, match: (v) => v === "patients" || v === "patient" },
      { key: "vitals", label: "Vitals & I/O", icon: <MonitorHeartIcon fontSize="small" /> },
      { key: "emar", label: "eMAR", icon: <MedicationIcon fontSize="small" /> },
      { key: "receive", label: "Receive Meds", icon: <LocalPharmacyIcon fontSize="small" /> },
      { key: "tasks", label: "My Tasks", icon: <AssignmentTurnedInIcon fontSize="small" /> },
      { key: "theatre", label: "Theatre Calls", icon: <LocalHospitalIcon fontSize="small" /> },
      { key: "handover", label: "Shift Handover", icon: <AssignmentIndIcon fontSize="small" /> },
      { key: "schedule", label: "My Schedule", icon: <CalendarMonthIcon fontSize="small" /> },
    ],
    // "patient" has no nav item — it is reached from the patient cards.
    views: ["home", "patients", "patient", "vitals", "emar", "receive", "tasks", "theatre", "handover", "schedule"],
  },
  manager: {
    title: "Nursing Manager",
    subtitle: "Ward Operations",
    heading: "Nursing Manager",
    defaultView: "manager",
    items: [
      { key: "manager", label: "Bed Assignments", icon: <HotelIcon fontSize="small" /> },
      { key: "transfers", label: "Transfer History", icon: <SwapHorizIcon fontSize="small" /> },
      { key: "roster", label: "Staff Roster", icon: <SupervisorAccountIcon fontSize="small" /> },
    ],
    views: ["manager", "transfers", "roster"],
  },
};

function SidebarContent({ section, view, onNavigate, nurseName, roleInfo, collapsed = false, onToggleCollapse }) {
  return (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: "100%",
        bgcolor: NURSE_COLORS.primary,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        // No width transition: animating width relayouts the entire content
        // area every frame, and on the dense views (the 260-bed census grid)
        // that drops frames. The collapse snaps instead.
        overflowX: "hidden",
      }}
    >
      <Box sx={{ p: collapsed ? 1 : 2, display: "flex", alignItems: "center", gap: 1.25, justifyContent: collapsed ? "center" : "flex-start" }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(33,205,192,0.15)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <LocalHospitalIcon sx={{ color: NURSE_COLORS.accent, fontSize: 22 }} />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "#fff", lineHeight: 1.1 }} noWrap>{section.title}</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }} noWrap>{section.subtitle}</Typography>
          </Box>
        )}
      </Box>

      {/* Desktop only: the mobile drawer closes instead of collapsing. */}
      {onToggleCollapse && (
        <Box sx={{ px: collapsed ? 0 : 1, pb: 0.5, display: "flex", justifyContent: collapsed ? "center" : "flex-end" }}>
          <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              sx={{ color: "rgba(255,255,255,0.75)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
            >
              {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 1, pb: 2 }}>
        <List
          dense
          disablePadding
          subheader={
            collapsed ? null : (
              <ListSubheader
                disableSticky
                sx={{ bgcolor: "transparent", color: "rgba(255,255,255,0.5)", fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", lineHeight: 2.2, px: 1.5 }}
              >
                {section.heading}
              </ListSubheader>
            )
          }
          sx={{ mb: 1 }}
        >
          {section.items.map((item) => {
            const active = item.match ? item.match(view) : view === item.key;
            const button = (
              <ListItemButton
                key={item.key}
                onClick={() => onNavigate(item.key)}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.25,
                  px: collapsed ? 0 : 1.5,
                  py: 0.9,
                  justifyContent: collapsed ? "center" : "flex-start",
                  color: active ? "#fff" : "rgba(255,255,255,0.78)",
                  bgcolor: active ? "rgba(33,205,192,0.18)" : "transparent",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  borderLeft: active ? `3px solid ${NURSE_COLORS.accent}` : "3px solid transparent",
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: collapsed ? 0 : 32, justifyContent: "center" }}>{item.icon}</ListItemIcon>
                {!collapsed && (
                  <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: active ? 600 : 500 } }} />
                )}
              </ListItemButton>
            );
            // Collapsed, the icon is the only label there is — without a
            // tooltip the nav becomes a guessing game.
            return collapsed ? (
              <Tooltip key={item.key} title={item.label} placement="right">
                <Box>{button}</Box>
              </Tooltip>
            ) : (
              button
            );
          })}
        </List>
      </Box>

      <Box sx={{ p: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 1, justifyContent: collapsed ? "center" : "flex-start" }}>
        <Tooltip title={collapsed ? `${nurseName || "Nurse"} · On shift` : ""} placement="right">
          <Avatar sx={{ bgcolor: NURSE_COLORS.accent, width: 34, height: 34, fontSize: 14, flexShrink: 0 }}>{initials(nurseName)}</Avatar>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ color: "#fff", lineHeight: 1.1 }} noWrap>{nurseName || "Nurse"}</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
              {roleInfo?.label || "Staff Nurse"} · On shift
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const VIEWS = {
  home: DashboardHome,
  patients: MyPatients,
  patient: PatientDetail,
  vitals: VitalsOverview,
  emar: EmarOverview,
  receive: PendingConfirmations,
  tasks: MyTasks,
  theatre: TheatreCalls,
  manager: BedDashboard,
  transfers: TransferHistory,
  roster: StaffRoster,
  handover: ShiftHandover,
  schedule: MySchedule,
};

export default function NurseDashboardShell({ section: sectionKey = "nurse" }) {
  const { userInfo } = usePermissions();
  const roleInfo = useMemo(
    () => getEffectiveNurseRole(userInfo?.role_permission?.[0]?.role),
    [userInfo]
  );

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      // Private-mode / blocked storage: fall back to expanded rather than
      // breaking the shell over a preference.
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* preference just won't persist */
      }
      return next;
    });
  }, []);

  // Theatre Calls exists only where the hospital has OT. A tenant without OT
  // tables answers OT_NOT_SET_UP; anything else — calls, another failure, a
  // network error — keeps the item, so an outage never hides it (QA ND #8).
  // The probe runs in the background, never behind the full-screen loader, and
  // a "not set up" answer is kept for the login session so it is asked once.
  const [otSetUp, setOtSetUp] = useState(() => sessionStorage.getItem(OT_NOT_SET_UP_KEY) !== "1");
  useEffect(() => {
    if (sectionKey !== "nurse" || !otSetUp) return undefined;
    let alive = true;
    getTheatreCalls(undefined, { skipGlobalLoader: true })
      .then((res) => {
        if (alive && res?.status === "Failed" && res?.message === OT_NOT_SET_UP) {
          sessionStorage.setItem(OT_NOT_SET_UP_KEY, "1");
          setOtSetUp(false);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  const baseSection = useMemo(() => {
    const s = SECTIONS[sectionKey] || SECTIONS.nurse;
    if (otSetUp) return s;
    return {
      ...s,
      items: s.items.filter((i) => i.key !== "theatre"),
      views: s.views.filter((v) => v !== "theatre"),
    };
  }, [sectionKey, otSetUp]);
  const section = useMemo(() => {
    if (sectionKey === "manager") {
      if (roleInfo.isSuperintendent) {
        return {
          ...baseSection,
          title: "Nursing Administration",
          subtitle: "Superintendent Console",
          heading: "Hospital Nursing Administration",
        };
      }
      return {
        ...baseSection,
        title: "Ward Operations",
        subtitle: "Ward Incharge Hub",
        heading: "Ward Incharge Manager",
      };
    }
    return baseSection;
  }, [baseSection, sectionKey, roleInfo.isSuperintendent]);

  const [view, setView] = useState(section.defaultView);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState(null);

  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const currentNurseName = sessionStorage.getItem("userName") || "Nurse";
  const currentNurseId = sessionStorage.getItem("doctor_id") || currentNurseName;

  const navigate = useCallback((nextView, params = {}) => {
    if (params.id != null) setSelectedAdmissionId(String(params.id));
    setView(nextView);
    setDrawerOpen(false);
  }, []);

  const reloadPatients = useCallback(async (filters = {}) => {
    setPatientsLoading(true);
    try {
      const payload = await getAdmissions(nurseAdmissionFilters(filters));
      const list = listOf(payload)
        .map(normalizeAdmission)
        .filter((p) => p.status !== "Discharged");
      setPatients(list);
      return list;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load admissions:", e);
      return [];
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    // reloadPatients scopes every load to Allocated patients — avoids pulling every
    // admission in the tenant and sending all IDs to bulk endpoints like med_admin
    // and vitals.
    reloadPatients();
  }, [reloadPatients]);

  const handleDateChange = useCallback((from, to) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const ctxValue = useMemo(
    () => ({
      patients,
      patientsLoading,
      reloadPatients,
      currentNurseName,
      currentNurseId,
      selectedAdmissionId,
      navigate,
      view,
      dateFrom,
      dateTo,
    }),
    [patients, patientsLoading, reloadPatients, currentNurseName, currentNurseId, selectedAdmissionId, navigate, view, dateFrom, dateTo]
  );

  const ActiveView = (section.views.includes(view) && VIEWS[view]) || VIEWS[section.defaultView];

  // Hold the view's ELEMENT identity stable across shell re-renders. Without
  // this, chrome-only state (collapsing the sidebar) re-renders the whole
  // active view — and on Bed Assignments that is a 260-bed grid rebuilt just to
  // change a width, which is what made the collapse feel laggy. React bails out
  // of re-rendering a child whose element is referentially unchanged; the views
  // read their data from context, so they still update when the data does.
  //
  // exhaustive-deps calls ActiveView an unnecessary dependency and is wrong:
  // it does not see a capitalised JSX identifier as a reference. Dropping it
  // would pin this memo to whichever view happened to be active first, so
  // changing section would keep rendering the old one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeViewElement = useMemo(() => <ActiveView />, [ActiveView]);

  if (sectionKey === "manager" && roleInfo.isStaffNurse) {
    return <Redirect to="/nurse_dashboard" />;
  }

  return (
    <ThemeProvider theme={nurseTheme}>
      <CssBaseline />
      <NurseDataContext.Provider value={ctxValue}>
        <Box sx={{ minHeight: "100vh", bgcolor: NURSE_COLORS.pageBg, display: "flex" }}>
          {isDesktop ? (
            <Box
              component="nav"
              sx={{
                width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
                flexShrink: 0,
                position: "sticky",
                top: 0,
                height: "100vh",
              }}
            >
              <SidebarContent
                section={section}
                view={view}
                onNavigate={navigate}
                nurseName={currentNurseName}
                roleInfo={roleInfo}
                collapsed={collapsed}
                onToggleCollapse={toggleCollapsed}
              />
            </Box>
          ) : (
            <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { bgcolor: NURSE_COLORS.primary, border: 0 } }}>
              <SidebarContent section={section} view={view} onNavigate={navigate} nurseName={currentNurseName} roleInfo={roleInfo} />
            </Drawer>
          )}

          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {!isDesktop && (
              <AppBar position="sticky" elevation={0} sx={{ bgcolor: NURSE_COLORS.primary }}>
                <Toolbar sx={{ gap: 1.5, minHeight: 56 }}>
                  <IconButton edge="start" sx={{ color: "#fff" }} onClick={() => setDrawerOpen(true)}>
                    <MenuIcon />
                  </IconButton>
                  <LocalHospitalIcon sx={{ color: NURSE_COLORS.accent }} />
                  <Typography variant="subtitle1" sx={{ color: "#fff", flex: 1 }} noWrap>{section.title}</Typography>
                  <Chip size="small" avatar={<Avatar sx={{ bgcolor: NURSE_COLORS.accent }}>{initials(currentNurseName)}</Avatar>} label={currentNurseName} sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }} />
                </Toolbar>
              </AppBar>
            )}
            <Box sx={{ p: { xs: 1.5, md: 3 }, flex: 1, minWidth: 0 }}>
              {/* "tasks" (My Tasks) now owns its own date filter, defaulted to today (D12),
                  and "handover" (Shift Handover) owns its own shift-date filter (D11) —
                  every other nurse view still shares this header filter, so it stays for
                  them. In the manager section only Transfer History reads it: Bed
                  Assignments ignored it, and QA read "18-09-2026" as filtering the Beds
                  list (QA NM #2). */}
              {(sectionKey === "manager"
                ? view === "transfers"
                : view !== "patient" && view !== "tasks" && view !== "handover") && (
                <Stack direction="row" sx={{ alignItems: "center", justifyContent: "flex-end", mb: 1.5 }}>
                  <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
                </Stack>
              )}
              {activeViewElement}
            </Box>
          </Box>
        </Box>
      </NurseDataContext.Provider>
    </ThemeProvider>
  );
}
